import {VueController} from '@/service/support/vuex-controller';
import vaultService from '@/service/vault-service';

export default VueController("SignatureTracking", {
    data() {
        return {
            signatureRequests: [],
            portals: [],
            loading: false,
            statusFilter: 'all',
            portalFilter: null,
            searchQuery: '',
            recipientFilter: 'all',
            showDetailsDialog: false,
            selectedRequest: null,
            statusOptions: [
                { text: 'All', value: 'all' },
                { text: 'Pending', value: 'pending' },
                { text: 'Partially Complete', value: 'partially_completed' },
                { text: 'Completed', value: 'completed' },
                { text: 'Expired', value: 'expired' },
                { text: 'Cancelled', value: 'cancelled' }
            ],
            recipientFilterOptions: [
                { text: 'All Requests', value: 'all' },
                { text: 'Needs My Signature', value: 'my_pending' },
                { text: 'I\'m a Recipient', value: 'my_requests' }
            ],
            headers: [
                { text: 'File Name', value: 'file_name', sortable: true },
                { text: 'Portal', value: 'portal_name', sortable: true },
                { text: 'Sent By', value: 'sender_name', sortable: true },
                { text: 'Sent Date', value: 'created_at', sortable: true },
                { text: 'Expires', value: 'expires_at', sortable: true },
                { text: 'Recipients', value: 'recipients', sortable: false },
                { text: 'Status', value: 'status', sortable: true },
                { text: 'Actions', value: 'actions', sortable: false, align: 'center' }
            ],
            extendingRequestId: null
        };
    },
    state: [
        "organization",
        "user",
        "cognito"
    ],
    computed: {
        currentUserEmail() {
            if (this.cognito && this.cognito.attributes && this.cognito.attributes.email) {
                return this.cognito.attributes.email.toLowerCase();
            }
            return null;
        },
        portalOptions() {
            const options = this.portals.map(portal => ({
                text: portal.name,
                value: portal.id
            }));
            return [{ text: 'All Portals', value: null }, ...options];
        },
        filteredRequests() {
            let filtered = [...this.signatureRequests];

            // Recipient filter
            if (this.recipientFilter && this.recipientFilter !== 'all' && this.currentUserEmail) {
                filtered = filtered.filter(req => {
                    const userRecipient = this.getUserRecipient(req);
                    const orgRecipient = this.getOrganizationRecipient(req);
                    const anyGuestRecipient = this.canSignViaAnyGuest(req) ? this.getAnyGuestRecipient(req) : null;
                    
                    // User must be either a direct recipient, organization member, or able to sign via "Any Guest"
                    if (!userRecipient && !orgRecipient && !anyGuestRecipient) return false;
                    
                    if (this.recipientFilter === 'my_pending') {
                        // Show if user's status is pending OR organization's status is pending OR "Any Guest" is pending
                        return (userRecipient && userRecipient.status === 'pending') ||
                               (orgRecipient && orgRecipient.status === 'pending') ||
                               (anyGuestRecipient && anyGuestRecipient.status === 'pending');
                    } else if (this.recipientFilter === 'my_requests') {
                        // Show if user is a direct recipient OR organization member OR can sign via "Any Guest"
                        return true;
                    }
                    return false;
                });
            }

            // Status filter
            if (this.statusFilter && this.statusFilter !== 'all') {
                filtered = filtered.filter(req => req.status === this.statusFilter);
            }

            // Portal filter
            if (this.portalFilter) {
                filtered = filtered.filter(req => req.portal_id === this.portalFilter);
            }

            // Search filter
            if (this.searchQuery) {
                const query = this.searchQuery.toLowerCase();
                filtered = filtered.filter(req => {
                    const fileNameMatch = (req.file_name || '').toLowerCase().includes(query);
                    const recipientMatch = req.recipients && req.recipients.some(r => 
                        (r.email || '').toLowerCase().includes(query) ||
                        (r.name || '').toLowerCase().includes(query)
                    );
                    return fileNameMatch || recipientMatch;
                });
            }

            return filtered;
        }
    },
    mounted() {
        // Handle query parameters for initial filtering
        if (this.$route.query.search) {
            this.searchQuery = this.$route.query.search;
        }
        if (this.$route.query.portalId) {
            this.portalFilter = this.$route.query.portalId;
        }

        this.loadData();
    },
    methods: {
        async loadData() {
            this.loading = true;
            try {
                // Load portals (includes portals from user's org AND portals where user is a guest)
                const portalsResponse = await vaultService.query('getVaultsByOrgId', {}, undefined, {vuexCache: false});
                this.portals = portalsResponse || [];

                // Load signature requests from multiple sources
                const allRequests = [];
                const seenRequestIds = new Set();

                // 1. Load requests from user's own organization
                if (this.organization && this.organization.id) {
                    try {
                        const orgRequests = await vaultService.query('getSignatureRequestsByOrgId', {
                            org_id: this.organization.id
                        }, undefined, {vuexCache: false});
                        
                        if (orgRequests && orgRequests.length > 0) {
                            orgRequests.forEach(req => {
                                if (!seenRequestIds.has(req.id)) {
                                    seenRequestIds.add(req.id);
                                    allRequests.push(req);
                                }
                            });
                        }
                    } catch (orgError) {
                        console.error('Error loading org signature requests:', orgError);
                    }
                }

                // 2. Load requests from all portals user has access to (including guest portals)
                if (this.portals && this.portals.length > 0) {
                    const portalRequestPromises = this.portals.map(portal =>
                        vaultService.query('getSignatureRequestsByPortalId', {
                            portal_id: portal.id
                        }, undefined, {vuexCache: false})
                        .catch(err => {
                            console.error(`Error loading requests for portal ${portal.name}:`, err);
                            return [];
                        })
                    );

                    const portalRequestsArrays = await Promise.all(portalRequestPromises);
                    
                    portalRequestsArrays.forEach(portalRequests => {
                        if (portalRequests && portalRequests.length > 0) {
                            portalRequests.forEach(req => {
                                if (!seenRequestIds.has(req.id)) {
                                    seenRequestIds.add(req.id);
                                    allRequests.push(req);
                                }
                            });
                        }
                    });
                }

                // Sort by created_at descending (newest first)
                allRequests.sort((a, b) => {
                    return new Date(b.created_at) - new Date(a.created_at);
                });

                // Debug: Log first request to check fields
                if (allRequests.length > 0) {
                    console.log('[SignatureTracking] Sample request data:', {
                        sender_name: allRequests[0].sender_name,
                        sender_email: allRequests[0].sender_email,
                        org_name: allRequests[0].org_name,
                        org_logo: allRequests[0].org_logo,
                        file_name: allRequests[0].file_name,
                        file_deleted: allRequests[0].file_deleted,
                        file_id: allRequests[0].file_id
                    });
                    console.log('[SignatureTracking] File deletion status for all requests:', 
                        allRequests.map(r => ({
                            file_name: r.file_name,
                            file_id: r.file_id,
                            file_deleted: r.file_deleted
                        }))
                    );
                }

                this.signatureRequests = allRequests;
            } catch (error) {
                console.error('[SignatureTracking] Error loading signature tracking data:', error);
            } finally {
                this.loading = false;
            }
        },
        async refreshData() {
            await this.loadData();
        },
        getPortalName(portalId) {
            const portal = this.portals.find(p => p.id === portalId);
            return portal ? portal.name : 'Unknown Portal';
        },
        formatDate(dateString) {
            if (!dateString) return '-';
            const date = new Date(dateString);
            return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        },
        getStatusColor(status) {
            const colors = {
                'pending': 'orange',
                'partially_completed': 'blue',
                'completed': 'success',
                'expired': 'grey',
                'cancelled': 'error'
            };
            return colors[status] || 'grey';
        },
        getStatusText(status) {
            const texts = {
                'pending': 'Pending',
                'partially_completed': 'Partially Complete',
                'completed': 'Completed',
                'expired': 'Expired',
                'cancelled': 'Cancelled'
            };
            return texts[status] || status;
        },
        getRecipientStatusColor(status) {
            if (status === 'completed') return 'success';
            if (status === 'pending') return 'orange';
            return 'grey';
        },
        getRecipientStatusIcon(status) {
            if (status === 'completed') return 'mdi-check-circle';
            if (status === 'pending') return 'mdi-clock-outline';
            return 'mdi-help-circle';
        },
        viewDetails(item) {
            this.selectedRequest = item;
            this.showDetailsDialog = true;
        },
        async resendRequest(item) {
            const confirmed = confirm('Resend signature request to all pending recipients?');
            if (confirmed) {
                try {
                    const result = await vaultService.mutation('resendSignatureRequest', {
                        signature_request_id: item.id
                    });
                    this.notification('success', result || 'Signature request resent successfully');
                    await this.loadData();
                } catch (error) {
                    console.error('Error resending signature request:', error);
                    this.notification('error', 'Failed to resend signature request: ' + (error.message || 'Unknown error'));
                }
            }
        },
        async cancelRequest(item) {
            const confirmed = confirm('Are you sure you want to cancel this signature request? Recipients will be notified of the cancellation.');
            if (confirmed) {
                try {
                    await vaultService.mutation('cancelSignatureRequest', {
                        signature_request_id: item.id
                    });
                    this.notification('success', 'Signature request cancelled successfully');
                    await this.loadData();
                } catch (error) {
                    console.error('Error canceling signature request:', error);
                    this.notification('error', 'Failed to cancel signature request: ' + (error.message || 'Unknown error'));
                }
            }
        },
        async deleteRequest(item) {
            const confirmed = confirm('Are you sure you want to permanently delete this signature request? This action cannot be undone.');
            if (confirmed) {
                try {
                    await vaultService.mutation('deleteSignatureRequest', {
                        signature_request_id: item.id
                    });
                    this.notification('success', 'Signature request deleted successfully');
                    await this.loadData();
                } catch (error) {
                    console.error('Error deleting signature request:', error);
                    this.notification('error', 'Failed to delete signature request: ' + (error.message || 'Unknown error'));
                }
            }
        },
        getUserRecipient(request) {
            if (!this.currentUserEmail || !request.recipients) {
                return null;
            }
            return request.recipients.find(r => 
                r.email && r.email.toLowerCase() === this.currentUserEmail
            );
        },
        getOrganizationRecipient(request) {
            // Check if there's an organization recipient that matches the user's organization
            if (!this.organization || !this.organization.id || !request.recipients) {
                return null;
            }
            const orgEmail = `org:${this.organization.id}`;
            return request.recipients.find(r => 
                r.email && r.email === orgEmail
            );
        },
        getAnyGuestRecipient(request) {
            // Check if there's an "any_guest" recipient
            if (!request.recipients) {
                return null;
            }
            return request.recipients.find(r => r.email === 'any_guest');
        },
        canSignViaAnyGuest(request) {
            // Check if the current user can sign via "Any Guest"
            // This is true if:
            // 1. The signature request is from a DIFFERENT organization than the user's, OR
            // 2. The user has "guest" type permissions on the portal
            // 3. AND the user has access to the portal (has permissions)
            
            if (!this.currentUserEmail || !request.portal_id || !this.portals) {
                return false;
            }
            
            // Find the portal for this request
            const portal = this.portals.find(p => p.id === request.portal_id);
            if (!portal || !portal.permissions) {
                return false;
            }
            
            // Check if user has access to this portal
            const userPermission = portal.permissions.find(perm => 
                perm.user_email && perm.user_email.toLowerCase() === this.currentUserEmail && 
                perm._deleted !== 1
            );
            
            if (!userPermission) {
                return false;
            }
            
            // Check if the signature request is from a different organization
            const isDifferentOrg = this.organization && request.org_id && 
                                   this.organization.id !== request.org_id;
            
            // User can sign via "Any Guest" if:
            // - They have explicit guest permissions, OR
            // - The request is from a different org (cross-org guest scenario)
            return userPermission.user_type === 'guest' || isDifferentOrg;
        },
        isUserRecipient(request) {
            return !!this.getUserRecipient(request);
        },
        canUserSign(request) {
            // Check if user is a direct recipient
            const userRecipient = this.getUserRecipient(request);
            if (userRecipient) {
                // Cannot sign if file is deleted
                if (request.file_deleted) return false;
                // Can sign if user's status is pending and request is active
                return userRecipient.status === 'pending' && 
                       request.status !== 'completed' && 
                       request.status !== 'cancelled' &&
                       request.status !== 'expired';
            }
            
            // Check if user can sign on behalf of organization
            const orgRecipient = this.getOrganizationRecipient(request);
            if (orgRecipient) {
                // Cannot sign if file is deleted
                if (request.file_deleted) return false;
                // Can sign if organization's status is pending and request is active
                return orgRecipient.status === 'pending' && 
                       request.status !== 'completed' && 
                       request.status !== 'cancelled' &&
                       request.status !== 'expired';
            }
            
            // Check if user can sign via "Any Guest" and there's an "Any Guest" recipient
            if (this.canSignViaAnyGuest(request)) {
                const anyGuestRecipient = this.getAnyGuestRecipient(request);
                if (anyGuestRecipient) {
                    // Cannot sign if file is deleted
                    if (request.file_deleted) return false;
                    // Can sign if "Any Guest" status is pending and request is active
                    return anyGuestRecipient.status === 'pending' && 
                           request.status !== 'completed' && 
                           request.status !== 'cancelled' &&
                           request.status !== 'expired';
                }
            }
            
            return false;
        },
        handleFileClick(item) {
            // If file is deleted, show details dialog with warning
            if (item.file_deleted) {
                this.viewDetails(item);
                return;
            }
            
            // If user can sign, navigate to signature page
            if (this.canUserSign(item)) {
                // First check if user is a direct recipient
                const userRecipient = this.getUserRecipient(item);
                if (userRecipient && userRecipient.token) {
                    // Navigate using the user's token
                    this.$router.push({
                        path: `/pdf-workbench/sign/${item.id}/${userRecipient.token}`
                    });
                    return;
                }
                
                // Check if user can sign on behalf of organization
                const orgRecipient = this.getOrganizationRecipient(item);
                if (orgRecipient && orgRecipient.token) {
                    // Navigate using the organization's token
                    this.$router.push({
                        path: `/pdf-workbench/sign/${item.id}/${orgRecipient.token}`
                    });
                    return;
                }
                
                // Check if user can sign via "Any Guest"
                if (this.canSignViaAnyGuest(item)) {
                    const anyGuestRecipient = this.getAnyGuestRecipient(item);
                    if (anyGuestRecipient && anyGuestRecipient.token) {
                        // Navigate using the "Any Guest" token
                        this.$router.push({
                            path: `/pdf-workbench/sign/${item.id}/${anyGuestRecipient.token}`
                        });
                        return;
                    }
                }
                
                // If we get here, something went wrong
                this.notification('error', 'Unable to access signature request. Token not found.');
            } else {
                // Check if user or organization has already signed
                const userRecipient = this.getUserRecipient(item);
                const orgRecipient = this.getOrganizationRecipient(item);
                const anyGuestRecipient = this.canSignViaAnyGuest(item) ? this.getAnyGuestRecipient(item) : null;
                
                if ((userRecipient && userRecipient.status === 'completed') || 
                    (orgRecipient && orgRecipient.status === 'completed') ||
                    (anyGuestRecipient && anyGuestRecipient.status === 'completed')) {
                    // Already signed - show details dialog
                    this.viewDetails(item);
                } else {
                    // Not a recipient or can't sign - show details dialog
                    this.viewDetails(item);
                }
            }
        },
        getRecipientDisplayName(recipient, request) {
            // Check if the email is "any_guest"
            if (recipient.email === 'any_guest') {
                const portalName = request && request.portal_name ? request.portal_name : 'this portal';
                return `Any guest of the ${portalName} portal`;
            }
            // Check if the email is an organization participant
            if (recipient.email && recipient.email.startsWith('org:')) {
                const orgName = request && request.org_name ? request.org_name : 'this organization';
                return `Any member of the ${orgName} organization`;
            }
            return recipient.name || recipient.email;
        },
        getRecipientDisplayEmail(recipient) {
            // Check if this is "any_guest"
            if (recipient.email === 'any_guest') {
                return 'Guest-wide participant';
            }
            // Don't display the "org:..." GUID for organization participants
            if (recipient.email && recipient.email.startsWith('org:')) {
                return 'Organization-wide participant';
            }
            return recipient.email;
        },
        userBelongsToRequestOrg(request) {
            // Check if the current user belongs to the same organization as the signature request
            if (!this.organization || !this.organization.id || !request.org_id) {
                return false;
            }
            return this.organization.id === request.org_id;
        },
        isRequestSenderOrAdmin(request) {
            // Check if user is the sender of this request or an org admin
            if (!this.user || !this.user.id) return false;
            const isSender = request.sender_user_id === this.user.id;
            const isAdmin = this.organization && this.organization.administrator_user_id === this.user.id;
            return isSender || isAdmin;
        },
        isExpired(request) {
            if (!request.expires_at) return false;
            return new Date(request.expires_at) < new Date();
        },
        isExpiringSoon(request) {
            if (!request.expires_at) return false;
            const expiresAt = new Date(request.expires_at);
            const now = new Date();
            const daysRemaining = (expiresAt - now) / (1000 * 60 * 60 * 24);
            return daysRemaining > 0 && daysRemaining <= 7;
        },
        formatExpiresDate(dateString) {
            if (!dateString) return '-';
            return new Date(dateString).toLocaleDateString();
        },
        daysUntilExpiration(request) {
            if (!request.expires_at) return null;
            const expiresAt = new Date(request.expires_at);
            const now = new Date();
            return Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));
        },
        canExtendExpiration(request) {
            // Only sender or admin can extend, and only for active (non-completed, non-cancelled) requests
            return this.isRequestSenderOrAdmin(request) &&
                   request.status !== 'completed' &&
                   request.status !== 'cancelled';
        },
        async extendExpiration(item) {
            const daysLeft = this.daysUntilExpiration(item);
            let message = 'Extend the expiration by 30 days and resend the signature request to all pending recipients?';
            if (daysLeft !== null && daysLeft <= 0) {
                message = 'This signature request has expired. Extend the expiration by 30 days from today and resend to all pending recipients?';
            }
            const confirmed = confirm(message);
            if (confirmed) {
                this.extendingRequestId = item.id;
                try {
                    const result = await vaultService.mutation('resendSignatureRequest', {
                        signature_request_id: item.id
                    });
                    this.notification('success', result || 'Expiration extended and request resent successfully');
                    await this.loadData();
                } catch (error) {
                    console.error('Error extending signature request:', error);
                    this.notification('error', 'Failed to extend signature request: ' + (error.message || 'Unknown error'));
                } finally {
                    this.extendingRequestId = null;
                }
            }
        }
    }
});

